import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UnitType } from "../../models/warehouseRawMaterial.model";
import {
  assignDefectiveResolutionApi,
  completeVendorResolutionApi,
  createDefectiveMaterialRequestApi,
  fetchDefectiveRequestsApi,
  reviewDefectiveRequestApi,
} from "../../api/warehouseDefectiveMaterial.api";

// --- Interfaces ---

export interface IDefectiveActivityLog {
  date: string;
  action: string;
  userId: string;
  remark: string;
}

export interface DefectiveMaterialRequest {
  _id: string;
  rawMaterialId: {
    _id: string;
    name: string;
    sku: string;
    unit: UnitType;
    unitType: "weight" | "liquid" | "length" | "piece";
  };
  warehouseId: { _id: string; name: string } | string;
  createdBy: { _id: string; username: string };
  quantity: number;
  unit: UnitType;
  reason: string;
  storeReviewStatus: "pending" | "accepted" | "rejected";
  vendorProcessStatus?:
    | "not_applicable"
    | "initiated"
    | "rejected"
    | "withdrawn"
    | "completed"; // VENDOR FLOW (only used for refund/replace)
  requestType?: "return" | "replace" | "refund" | "shrink";
  affectedBatches: { inwardRef: string; deductedQuantity: number }[];
  activityLog: IDefectiveActivityLog[];
  createdAt: string;
  updatedAt: string;
  replaceData?: {
    initiatedAt?: Date;
    receivedAt?: Date;
    quantityReceived?: number;
  };
  refundData?: {
    initiatedAt?: Date;
    refundedAt?: Date;
    refundAmount?: number;
  };
}

export interface DefectiveMaterialState {
  requests: DefectiveMaterialRequest[];
  loading: boolean;
  error: string | null;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

// --- Initial State ---

const initialState: DefectiveMaterialState = {
  requests: [],
  loading: false,
  error: null,
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  limit: 10,
};

// --- Async Thunks ---

// 1. Create Request (Bulk)
export const createDefectiveMaterialRequest = createAsyncThunk<
  DefectiveMaterialRequest[],
  {
    items: {
      materialId: string;
      quantity: number;
      unit: UnitType;
      remark: string;
    }[];
  }
>("defectiveMaterial/createRequest", async (payload, { rejectWithValue }) => {
  try {
    return await createDefectiveMaterialRequestApi(payload);
  } catch (err: any) {
    return rejectWithValue(
      err.message || "Failed to create defective material request"
    );
  }
});

// 2. Fetch Requests (Handles both History and Pending via storeStatus param)
export const fetchDefectiveRequests = createAsyncThunk<
  {
    items: DefectiveMaterialRequest[];
    totalItems: number;
    totalPages: number;
    currentPage: number;
  },
  { page?: number; limit?: number; search?: string; storeStatus?: string }
>(
  "defectiveMaterial/fetchRequests",
  async (
    { page = 1, limit = 10, search = "", storeStatus },
    { rejectWithValue }
  ) => {
    try {
      return await fetchDefectiveRequestsApi({
        page,
        limit,
        search,
        storeStatus,
      });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch defective requests"
      );
    }
  }
);

// 3. Review Request (Accept/Reject)
export const reviewDefectiveRequest = createAsyncThunk<
  { requestId: string; status: "accepted" | "rejected" }, // return type
  { requestId: string; status: "accepted" | "rejected"; remark?: string }
>("defectiveMaterial/reviewRequest", async (payload, { rejectWithValue }) => {
  try {
    await reviewDefectiveRequestApi(payload);

    return { requestId: payload.requestId, status: payload.status };
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to review request");
  }
});

export const assignDefectiveResolution = createAsyncThunk<
  DefectiveMaterialRequest,
  {
    requestId: string;
    type: "return" | "replace" | "refund" | "shrink";
    remark?: string;
  }
>(
  "defectiveMaterial/assignResolution",
  async (payload, { rejectWithValue }) => {
    try {
      // You need to import this function from your API file
      return await assignDefectiveResolutionApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to assign resolution");
    }
  }
);

export const completeVendorResolution = createAsyncThunk<
  DefectiveMaterialRequest,
  {
    requestId: string;
    type: "replace" | "refund";
    quantity?: number;
    unit?: string;
    amount?: number;
    remark?: string;
  }
>(
  "defectiveMaterial/completeVendorResolution",
  async (payload, { rejectWithValue }) => {
    try {
      return await completeVendorResolutionApi(payload);
    } catch (err: any) {
      return rejectWithValue(err || "Failed to complete resolution");
    }
  }
);

// --- Slice ---

const defectiveMaterialSlice = createSlice({
  name: "defectiveMaterial",
  initialState,
  reducers: {
    clearDefectiveError: (state) => {
      state.error = null;
    },
    resetDefectiveState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Create Request Handling
      .addCase(createDefectiveMaterialRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDefectiveMaterialRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          state.requests.unshift(...action.payload);
          state.totalItems += action.payload.length;
        }
      })
      .addCase(createDefectiveMaterialRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Requests Handling
      .addCase(fetchDefectiveRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDefectiveRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload.items;
        state.totalItems = action.payload.totalItems;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchDefectiveRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Review Action Handling
      .addCase(reviewDefectiveRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(reviewDefectiveRequest.fulfilled, (state, action) => {
        state.loading = false;
        const { requestId, status } = action.payload;
        const index = state.requests.findIndex((r) => r._id === requestId);
        if (index !== -1) {
          state.requests[index].storeReviewStatus = status;
        }
      })
      .addCase(reviewDefectiveRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(assignDefectiveResolution.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignDefectiveResolution.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(
          (r) => r._id === action.payload._id
        );
        if (index !== -1) {
          state.requests[index].requestType = action.payload.requestType;
          state.requests[index].vendorProcessStatus =
            action.payload.vendorProcessStatus;
        }
      })
      .addCase(assignDefectiveResolution.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(completeVendorResolution.pending, (state) => {
        state.loading = true;
      })
      .addCase(completeVendorResolution.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(
          (r) => r._id === action.payload._id
        );
        if (index !== -1) {
          state.requests[index].vendorProcessStatus =
            action.payload.vendorProcessStatus;
          state.requests[index].replaceData =
            action.payload.replaceData || state.requests[index].replaceData;
          state.requests[index].refundData =
            action.payload.refundData || state.requests[index].refundData;
          state.requests[index].activityLog =
            action.payload.activityLog || state.requests[index].activityLog;
        }
      })
      .addCase(completeVendorResolution.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDefectiveError, resetDefectiveState } =
  defectiveMaterialSlice.actions;
export default defectiveMaterialSlice.reducer;
